import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Trash2, Loader2 } from 'lucide-react';
import {
  useEmailSequences,
  SequenceStepInput,
} from '../../hooks/useEmailSequences';

// Triggers the backend actually acts on.
//
// The list used to offer lead_captured, lead_qualified, settlement,
// anniversary and rate_decision. No backend code reads the `trigger` field at
// all: enrolment is manual from the lead inbox, whichever one was picked. An
// option that silently does nothing is worse than no option, so only the
// honest one is offered until auto-enrolment is built.
const TRIGGERS: Array<{ value: string; label: string }> = [
  { value: 'manual', label: 'Manual (enrol leads from the inbox)' },
];

interface Props {
  companyId: string | undefined;
  open: boolean;
  onClose: () => void;
  onCreated?: (sequenceId: string) => void;
}

export const CreateSequenceModal: React.FC<Props> = ({
  companyId,
  open,
  onClose,
  onCreated,
}) => {
  const { createSequence, templates, templatesLoading } = useEmailSequences(companyId);
  // Only templates every merge field of which resolves from real lead data.
  // A preview-only template would be blocked at send time, so it must not be
  // schedulable. The backend refuses one too; this keeps the UI honest about
  // why it is not on the list.
  const sendableTemplates = useMemo(
    () => templates.filter((t) => t.sendable),
    [templates],
  );
  const previewOnlyCount = templates.length - sendableTemplates.length;
  const defaultTemplateId = sendableTemplates[0]?.templateId ?? '';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [trigger, setTrigger] = useState<string>(TRIGGERS[0].value);
  const [steps, setSteps] = useState<SequenceStepInput[]>([
    { order: 1, dayOffset: 0, subject: '', templateId: '', active: true },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fill in the first template once the registry has loaded.
  useEffect(() => {
    if (!defaultTemplateId) return;
    setSteps((prev) =>
      prev.map((s) => (s.templateId ? s : { ...s, templateId: defaultTemplateId })),
    );
  }, [defaultTemplateId]);

  if (!open) return null;

  const addStep = () => {
    setSteps((prev) => [
      ...prev,
      {
        order: prev.length + 1,
        dayOffset: (prev[prev.length - 1]?.dayOffset ?? 0) + 3,
        subject: '',
        templateId: defaultTemplateId,
        active: true,
      },
    ]);
  };

  const removeStep = (index: number) => {
    setSteps((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, order: i + 1 }))
    );
  };

  const updateStep = (
    index: number,
    patch: Partial<SequenceStepInput>
  ) => {
    setSteps((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s))
    );
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (steps.some((s) => !s.subject.trim())) {
      setError('Every step needs a subject');
      return;
    }
    if (steps.some((s) => !s.templateId)) {
      setError('Every step needs a template');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const result = await createSequence({
        name: name.trim(),
        description: description.trim() || undefined,
        trigger,
        steps,
      });
      onCreated?.(result.sequenceId);
      onClose();
      setName('');
      setDescription('');
      setTrigger(TRIGGERS[0].value);
      setSteps([
        { order: 1, dayOffset: 0, subject: '', templateId: defaultTemplateId, active: true },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 sticky top-0 bg-gray-900 z-10">
          <h2 className="text-base font-semibold text-white">
            Create email sequence
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-800 text-gray-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. New lead welcome (3 emails)"
                className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1">
                Trigger
              </label>
              <select
                value={trigger}
                onChange={(e) => setTrigger(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-sm"
              >
                {TRIGGERS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-sm resize-none"
            />
          </div>
          {templatesLoading && (
            <div className="rounded-md border border-gray-700 bg-gray-800/40 p-3 flex items-center gap-2 text-xs text-gray-400">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Loading templates…
            </div>
          )}

          {!templatesLoading && sendableTemplates.length === 0 && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
              No template can be sent from lead data yet, so a sequence cannot
              be created. Templates that need loan details such as the lender
              or interest rate are preview-only until that data is available.
            </div>
          )}

          {!templatesLoading && previewOnlyCount > 0 && sendableTemplates.length > 0 && (
            <div className="rounded-md border border-gray-700 bg-gray-800/40 p-3 text-xs text-gray-400">
              {previewOnlyCount} further{' '}
              {previewOnlyCount === 1 ? 'template is' : 'templates are'}{' '}
              preview-only and not listed here. They reference loan details such
              as the lender, interest rate or dollar amounts, which we cannot
              read from a lead record, so they cannot be sent safely yet. You can
              still view them in the template gallery.
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-gray-400">
                Steps
              </label>
              <button
                type="button"
                onClick={addStep}
                disabled={steps.length >= 20}
                className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-[11px] inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Plus className="w-3 h-3" /> Add step
              </button>
            </div>
            <div className="space-y-2">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className="rounded-md border border-gray-700 bg-gray-800/40 p-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-white">
                      Step {step.order}
                    </span>
                    {steps.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeStep(index)}
                        className="p-1 rounded hover:bg-gray-700 text-red-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                        Day offset
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={3650}
                        value={step.dayOffset}
                        onChange={(e) =>
                          updateStep(index, {
                            dayOffset: Number(e.target.value) || 0,
                          })
                        }
                        className="w-full px-2 py-1 rounded bg-gray-900 border border-gray-700 text-white text-xs"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                        Template
                      </label>
                      <select
                        value={step.templateId}
                        onChange={(e) =>
                          updateStep(index, { templateId: e.target.value })
                        }
                        className="w-full px-2 py-1 rounded bg-gray-900 border border-gray-700 text-white text-xs"
                      >
                        {sendableTemplates.map((t) => (
                          <option key={t.templateId} value={t.templateId}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={step.subject}
                      onChange={(e) =>
                        updateStep(index, { subject: e.target.value })
                      }
                      placeholder={'{{firstName}} — next steps'}
                      className="w-full px-2 py-1 rounded bg-gray-900 border border-gray-700 text-white text-xs"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-300">
              {error}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-700 sticky bottom-0 bg-gray-900 z-10">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm text-gray-300 hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || templatesLoading || sendableTemplates.length === 0}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? 'Creating…' : 'Create sequence'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSequenceModal;
